// Kill switch for the inline Assist session card in the chat transcript (29 Oct 2026).
//
// The same recipe as lib/assistLink.ts (the sidebar's Assist / Browser Hub choice): one tiny file
// served beside the bundle, /build/assist-card.json, read at runtime:
//
//   {"v":1,"enabled":true}   on
//   {"v":1,"enabled":false}  off (the repo ships this one)
//
// Turning it off is one file write on the box (no rebuild, no restart): every open tab drops the
// card and stops asking the Assist watch service at its next read (on load, at most 60 s later, and
// each time the app comes back to the front). Everything except EXACTLY the "on" body means OFF:
// a missing file (nginx answers a missing /build/ file with 200 and the index HTML), bad JSON, an
// extra key, another version, a network error, an answer slower than 2.5 s. OFF is today's
// transcript, byte for byte, and not one request to /api/assist-watch.

const FLAG_URL = `${import.meta.env.BASE_URL}assist-card.json`;
/** Longer than this and the answer is "off". */
const READ_TIMEOUT_MS = 2500;
/** A fresh answer is reused this long, so the app coming to the front again and again costs nothing. */
const FRESH_MS = 60_000;

/** True only for a JSON object whose own keys are exactly `v` (=== 1) and `enabled` (=== true). */
export function parseAssistCardFlag(text: string): boolean {
  try {
    const body: unknown = JSON.parse(text);
    if (!body || typeof body !== "object" || Array.isArray(body)) return false;
    const keys = Object.keys(body).sort();
    if (keys.length !== 2 || keys[0] !== "enabled" || keys[1] !== "v") return false;
    const flag = body as { v?: unknown; enabled?: unknown };
    return flag.v === 1 && flag.enabled === true;
  } catch {
    return false;
  }
}

async function readFlag(): Promise<boolean> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), READ_TIMEOUT_MS);
  try {
    // The query keeps a CDN or proxy from answering with an old copy; no-store keeps the browser's own cache out.
    const res = await fetch(`${FLAG_URL}?t=${Date.now()}`, { cache: "no-store", credentials: "omit", signal: controller.signal });
    return res.ok ? parseAssistCardFlag(await res.text()) : false;
  } catch {
    return false;
  } finally {
    clearTimeout(timer);
  }
}

let last: { at: number; on: boolean } | null = null;
let pending: Promise<boolean> | null = null;

/** Never rejects. `true` only when the file says exactly "on". */
export function assistCardEnabled(): Promise<boolean> {
  if (last && Date.now() - last.at < FRESH_MS) return Promise.resolve(last.on);
  if (!pending) {
    pending = readFlag().then((on) => {
      last = { at: Date.now(), on };
      return on;
    }).finally(() => { pending = null; });
  }
  return pending;
}
